import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Medal, Crown, Share2, Check, Search, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { FriendSearch } from '@/components/FriendSearch';
import { usePlayerProfiles } from '@/hooks/usePlayerProfiles';

interface LeaderboardEntry {
  rank: number;
  display_name: string;
  preferred_sport: string;
  xp: number;
  level: number;
  streak: number;
  is_current_user: boolean;
  username: string | null;
}

const SPORT_FILTERS = [
  { key: 'all', label: '🏆 All' },
  { key: 'football', label: '🏈 Football' },
  { key: 'soccer', label: '⚽ Soccer' },
  { key: 'basketball', label: '🏀 Basketball' },
];

type LeaderboardMode = 'global' | 'friends' | 'team';

export function LeaderboardTab() {
  const { user } = useAuth();
  // Child accounts are kept inside their own team — never a global list of strangers.
  const { isPlayer } = usePlayerProfiles();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeSport, setActiveSport] = useState('all');
  const [mode, setMode] = useState<LeaderboardMode>('global');

  useEffect(() => {
    if (isPlayer) setMode('team');
  }, [isPlayer]);
  const [showSearch, setShowSearch] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase.from('profiles').select('invite_code').eq('user_id', user.id).single()
      .then(({ data }) => { if (data) setInviteCode((data as any).invite_code); });
  }, [user]);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    const { data, error } = mode === 'team'
      ? await supabase.rpc('get_team_leaderboard' as any, { result_limit: 100 })
      : await supabase.rpc(mode === 'friends' ? 'get_friends_leaderboard' : 'get_leaderboard', {
          sport_filter: activeSport,
          result_limit: 100,
        });

    if (error) {
      console.error('Leaderboard fetch error:', error);
      setEntries([]);
    } else {
      setEntries(
        (data || []).map((row: any) => ({
          rank: Number(row.rank),
          display_name: row.display_name || 'Anonymous',
          preferred_sport: row.preferred_sport || 'football',
          xp: row.xp ?? 0,
          level: row.level ?? 0,
          streak: row.streak ?? 0,
          is_current_user: row.is_current_user ?? false,
          username: row.username ?? null,
        }))
      );
    }
    setLoading(false);
  }, [activeSport, mode]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  const handleShare = async () => {
    const shareUrl = inviteCode
      ? `${window.location.origin}/invite/${inviteCode}`
      : window.location.origin;
    const shareText = "I'm training my game IQ on Sportara! Join me and see if you can beat my score 🏆";

    if (navigator.share) {
      try {
        await navigator.share({ title: 'Sportara', text: shareText, url: shareUrl });
      } catch {
        // user cancelled
      }
    } else {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      setCopied(true);
      toast.success('Invite link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-accent" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-muted-foreground" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-accent" />;
    return null;
  };

  const currentUserEntry = entries.find(e => e.is_current_user);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-3">
          <Trophy className="w-7 h-7 text-accent" />
        </div>
        <h2 className="font-display font-extrabold text-2xl">Leaderboard</h2>
        <p className="text-sm text-muted-foreground font-body mt-1">
          {mode === 'team' ? 'Your team, ranked by XP' : 'Top learners ranked by XP'}
        </p>
      </div>

      {/* Global / Friends toggle — hidden for child accounts (team-only view) */}
      {!isPlayer && (
      <div className="flex gap-1 bg-muted rounded-xl p-1">
        <button
          onClick={() => setMode('global')}
          className={`flex-1 py-2 rounded-lg text-sm font-display font-bold transition-all ${
            mode === 'global'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          🌍 Global
        </button>
        <button
          onClick={() => setMode('friends')}
          className={`flex-1 py-2 rounded-lg text-sm font-display font-bold transition-all ${
            mode === 'friends'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          👥 Friends
        </button>
      </div>
      )}

      {/* Friends mode: search + invite */}
      {mode === 'friends' && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button
              onClick={() => setShowSearch(!showSearch)}
              variant="outline"
              className="flex-1 rounded-xl font-display font-bold"
            >
              <Search className="w-4 h-4 mr-2" />
              Find Friends
            </Button>
            <Button
              onClick={handleShare}
              variant="outline"
              className="rounded-xl font-display font-bold"
            >
              {copied ? <Check className="w-4 h-4 mr-1" /> : <Share2 className="w-4 h-4 mr-1" />}
              {copied ? 'Copied!' : 'Invite'}
            </Button>
          </div>
          <FriendSearch
            open={showSearch}
            onClose={() => setShowSearch(false)}
            onFollowChange={fetchLeaderboard}
          />
        </div>
      )}

      {/* Sport filter tabs */}
      {!isPlayer && (
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {SPORT_FILTERS.map(sport => (
          <button
            key={sport.key}
            onClick={() => setActiveSport(sport.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-display font-bold whitespace-nowrap transition-all ${
              activeSport === sport.key
                ? 'bg-primary text-primary-foreground shadow-glow-primary'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {sport.label}
          </button>
        ))}
      </div>
      )}

      {/* Team empty state — child hasn't joined a team yet */}
      {mode === 'team' && !loading && entries.length === 0 && (
        <div className="text-center py-6 space-y-2">
          <p className="font-display font-bold text-sm">No team yet</p>
          <p className="text-xs text-muted-foreground font-body">
            Ask a parent to add your coach's Team Code — then you'll see your teammates here.
          </p>
        </div>
      )}

      {/* Invite friends (global mode only) */}
      {mode === 'global' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-primary/10 rounded-2xl p-4 flex items-center justify-between gap-3"
        >
          <div>
            <p className="font-display font-bold text-sm">Challenge Your Friends</p>
            <p className="text-xs text-muted-foreground font-body">Share an invite link and climb together!</p>
          </div>
          <Button
            onClick={handleShare}
            size="sm"
            className="font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl shrink-0"
          >
            {copied ? <Check className="w-4 h-4 mr-1" /> : <Share2 className="w-4 h-4 mr-1" />}
            {copied ? 'Copied!' : 'Invite'}
          </Button>
        </motion.div>
      )}

      {/* Your rank */}
      {currentUserEntry && (
        <div className="bg-secondary rounded-2xl p-4 text-secondary-foreground">
          <p className="text-xs font-body text-secondary-foreground/60 mb-1">Your Rank</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="display-number text-2xl text-warning">#{currentUserEntry.rank}</span>
              <div>
                <p className="font-display font-bold text-sm">{currentUserEntry.username || 'Anonymous'}</p>
                <p className="text-xs text-secondary-foreground/60 font-body"><span className="display-number">{currentUserEntry.xp.toLocaleString()}</span> XP · 🔥 <span className="display-number">{currentUserEntry.streak}</span> day streak</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty state for friends */}
      {mode === 'friends' && !loading && entries.length <= 1 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-6 space-y-3"
        >
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <UserPlus className="w-8 h-8 text-primary" />
          </div>
          <div>
            <p className="font-display font-bold text-sm">No friends yet</p>
            <p className="text-xs text-muted-foreground font-body mt-1">
              Search for friends by username or share your invite link!
            </p>
          </div>
          <Button
            onClick={() => setShowSearch(true)}
            className="rounded-xl font-display font-bold"
          >
            <Search className="w-4 h-4 mr-2" /> Find Friends
          </Button>
        </motion.div>
      )}

      {/* Leaderboard list */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-muted/50 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        mode === 'global' ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground font-body">
              {activeSport === 'all' ? 'No learners yet. Be the first!' : `No ${SPORT_FILTERS.find(s => s.key === activeSport)?.label.slice(2)} learners yet!`}
            </p>
          </div>
        ) : null
      ) : (
        <div className="space-y-2">
          {entries.map((entry, i) => (
            <motion.div
              key={`rank-${entry.rank}`}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.04 }}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-colors ${
                entry.is_current_user
                  ? 'bg-primary/5 border-primary/30'
                  : 'bg-card border-border'
              }`}
            >
              <div className="w-8 text-center shrink-0">
                {getRankIcon(entry.rank) || (
                  <span className="display-number text-sm text-muted-foreground">{entry.rank}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-display font-bold text-sm truncate ${entry.is_current_user ? 'text-primary' : ''}`}>
                  {entry.username || 'Anonymous'}
                  {entry.is_current_user && <span className="text-xs font-body text-muted-foreground ml-1">(you)</span>}
                </p>
                <p className="text-xs text-muted-foreground font-body">
                  Level <span className="display-number">{entry.level}</span> · 🔥 <span className="display-number">{entry.streak}</span>
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="display-number text-sm">{entry.xp.toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground font-body">XP</p>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
